import {
  UIButton,
  UIButtonSize,
  UIButtonVariant,
} from '@perpetua1/oasis-react';
import cn from 'classnames';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { useI18next } from 'gatsby-plugin-react-i18next';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { customDataLayerPush } from 'src/utils/analytics';
import { DL_EVENT, LABEL, PAGE_SLOT } from 'src/utils/analytics/constants';
import { URL } from '../../constants/destinations';
import { PAGE } from '../../constants/pages';
import { nav } from '../../utils/navigation';
import Search from '../Blog/Search';
import Button, { ButtonVariant } from '../Button';
import Hamburger from './Hamburger';
import MegaNav from './MegaNav';
import LogoWordmarkDark from './assets/logo-wordmark-dark.min.svg';
import LogoWordmarkWhite from './assets/logo-wordmark-white.min.svg';
import LogoWordmark from './assets/logo-wordmark.min.svg';
import { NavigationBarVariant } from './models';

interface NewNavigationProps {
  activeVariant?: string;
  localesAvailable?: {
    language: string;
    url: string;
    '@type': string;
  }[];
  page?: string;
  setActiveFormModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

const NewNavigation: FC<NewNavigationProps> = props => {
  const { activeVariant, localesAvailable, page, setActiveFormModal } = props;
  const [showMobileNav, setShowMobileNav] = useState<boolean>(false);
  const [initialPosition, setInitialPosition] = useState<number>(0);
  const prevScrollPos = useRef<number>(0);
  const [visibleNav, setVisibleNav] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [openSearch, setOpenSearch] = useState<boolean>(false);

  const { language } = useI18next();
  const { t } = useTranslation();

  const mainNavigation = useStaticQuery(graphql`
    query ($language: String) {
      locales: allLocale(filter: { language: { eq: $language } }) {
        edges {
          node {
            ns
            data
            language
          }
        }
      }
      allContentfulMegaNav(filter: { node_locale: { eq: $language } }) {
        nodes {
          id
          position
          navDropdownTitle
          link
          navDropdownId
          node_locale
          gridColumns
          menuCta {
            id
            title
            ctaDescription {
              raw
            }
            buttonId
            buttonText
            buttonLink
          }
          gridColumnItems {
            title
            columnWidth
            items {
              ... on ContentfulIconTextDescription {
                __typename
                id
                title
                link
                icon {
                  url
                  title
                }
                description {
                  description
                }
              }
              ... on ContentfulCta {
                __typename
                id
                text
                route
              }
              ... on ContentfulBlogCta {
                __typename
                id
                title
                ctaDescription {
                  raw
                }
                buttonId
                buttonText
                buttonLink
              }
              ... on ContentfulNavLinks {
                __typename
                itemId
                id
                link
                text
                icon {
                  url
                  title
                }
                badgeText
              }
            }
            contentful_id
            highlight
            backgroundColor
            hideOnMobile
            target
          }
        }
      }
    }
  `);

  const homepage = {
    'en-US': '/',
    'zh-CN': '/',
    de: '/de',
    es: '/es',
    ja: '/ja',
  };

  const handleScroll = () => {
    prevScrollPos.current = prevScrollPos?.current || 0;
    const currentScrollPos = window?.pageYOffset;
    if (currentScrollPos > 0) {
      setVisibleNav(prevScrollPos.current > currentScrollPos);
      prevScrollPos.current = currentScrollPos;

      setInitialPosition(window.scrollY);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const logo = useMemo(() => {
    switch (activeVariant) {
      case NavigationBarVariant.indigo:
        return <LogoWordmarkWhite />;
      case NavigationBarVariant.light:
        return <LogoWordmarkWhite />;
      case NavigationBarVariant.dark:
        return <LogoWordmarkDark />;
      default:
        return <LogoWordmark />;
    }
  }, [activeVariant]);

  const filterLocaleNav = mainNavigation.allContentfulMegaNav.nodes
    .filter(item => item.node_locale === language)
    .sort((a, b) => a.position - b.position);

  return (
    <div
      className={cn(
        'w-full z-50 p-1 fixed transition-all duration-250 ease-in transform translate-y-0',
        'lg:px-8 lg:top-0 lg:pt-4',
        showMobileNav && 'h-full !p-0',
        !visibleNav && !showMobileNav && '!translate-y-[-100%]'
      )}
    >
      <div
        className={cn(
          'max-w-7xl relative h-full flex flex-col transition mx-auto items-center bg-white rounded-xl border border-transparent',
          visibleNav &&
            initialPosition !== 0 &&
            'border border-gray-5 shadow-xl ease-in',
          showMobileNav && 'rounded-none border-none shadow-none'
        )}
      >
        <div className="flex justify-between items-center py-4 lg:py-0 w-full px-8">
          <Link to={homepage[language]}>
            {NavigationBarVariant.indigo ? <LogoWordmark /> : logo}
          </Link>
          <div
            className={cn(
              'hidden lg:block w-full lg:w-auto fixed lg:static',
              showMobileNav &&
                '!block top-[80px] right-0 overflow-y-auto h-full'
            )}
          >
            <MegaNav
              visibleNav={visibleNav}
              showMobileNav={showMobileNav}
              navItems={filterLocaleNav}
            />
          </div>
          <div
            className={cn(
              'bg-[#EEF2FF] px-2 py-4 hidden flex-row w-full absolute right-0 left-0 gap-4 bottom-0',
              showMobileNav && '!flex'
            )}
          >
            <UIButton
              variant={UIButtonVariant.primary}
              className="!w-1/2"
              size={UIButtonSize.large}
              text={page === PAGE.STARTUP ? 'Request Access' : t('Book a Demo')}
              onClick={() =>
                page === PAGE.STARTUP
                  ? setActiveFormModal && setActiveFormModal(true)
                  : nav(t(URL.Demo))
              }
            />
            <UIButton
              variant={UIButtonVariant.secondary}
              className="hover:bg-indigo-3-light mx-auto !w-1/2"
              size={UIButtonSize.large}
              text={'Sign In'}
              onClick={() => nav(t(URL.Login))}
            />
          </div>
          {/* Button section right side of nav */}
          <div className="flex items-center">
            <div className={cn('gap-5 hidden', 'lg:flex')}>
              <a
                href={t(URL.Login)}
                onClick={() =>
                  customDataLayerPush(DL_EVENT.elementClick, {pageSlot: PAGE_SLOT.Navi, label: LABEL.Signin })
                }
                className="text-gray-4-dark text-lg relative self-center font-semibold lg:hover:after:border-b-2 lg:hover:after:border-indigo-1 lg:hover:after:absolute lg:hover:after:bottom-[-2px] lg:hover:after:left-0  lg:hover:after:w-full transition-all"
              >
                {t('Sign In')}
              </a>

              <Button
                variant={ButtonVariant.primary}
                onClick={() => {
                  customDataLayerPush(DL_EVENT.elementClick, {pageSlot: PAGE_SLOT.Navi, label: LABEL.Demo });
                  page === PAGE.STARTUP
                    ? setActiveFormModal && setActiveFormModal(true)
                    : nav(t(URL.Demo));
                }}
              >
                {page === PAGE.STARTUP ? 'Request Access' : t('Book a Demo')}
                <svg
                  viewBox="0 0 16 16"
                  fill="#fff"
                  xmlns="http://www.w3.org/2000/svg"
                  className="fill-current inline-flex pl-2 justify-center items-center w-5 h-5"
                  width="12px"
                  height="12px"
                  fillRule="evenodd"
                >
                  <path
                    clipRule="evenodd"
                    d="M9.9 8 4.217 3.079a.646.646 0 0 1-.078-.886l.775-.967a.59.59 0 0 1 .852-.08l6.798 5.888c.276.239.435.593.435.966s-.16.727-.435.966l-6.798 5.888a.59.59 0 0 1-.852-.08l-.775-.967a.646.646 0 0 1 .078-.886L9.899 8Z"
                  ></path>
                </svg>
              </Button>
            </div>
            {page === PAGE.BLOG && (
              <div
                className="mx-3 cursor-pointer block lg:hidden"
                onClick={() => setOpenSearch(!openSearch)}
              >
                <svg
                  width={21}
                  height={21}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <path
                    fill="#6366F1"
                    d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"
                  />
                </svg>
              </div>
            )}

            {openSearch && (
              <div className="absolute left-0 right-0 top-[50px] rounded-md max-w-sm mx-auto py-6">
                {language === 'en-US' && (
                  <Search setSearchQuery={setSearchQuery} />
                )}
              </div>
            )}

            <Button
              onClick={() => {
                setShowMobileNav(!showMobileNav);
                /*
                hide Intercom widget if mega menu is open in mobile / tablet
                reason: widget overlaps w. 'Get Started' / 'Sign In' buttons
                */
                const intercomWidget = document.querySelector<HTMLElement>(
                  "div[class$='intercom-launcher'], #intercom-container"
                );
                if (intercomWidget !== null)
                  intercomWidget.style.display = !showMobileNav
                    ? 'none'
                    : 'block';
              }}
              className="block lg:hidden border-none !px-0"
            >
              <Hamburger showMobileNav={showMobileNav} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewNavigation;
